import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import AuthenticationPageTemplate from '../../templates/AuthenticationPageTemplate'
import EnterSecurityCode from '../../components/auth/EnterSecurityCode'
import SaveBackupCodes from '../../components/auth/SaveBackupCodes'
import TwoFactorAuthSetupCode from '../../components/auth/TwoFactorAuthSetupCode'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { useUser } from '../../context/CurrentUserContext'

const KEYCODE_LIFETIME = 5 * 60 * 1000

const TwoFactorAuthSetup = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const user = useUser()

  const [currentStepIndex, setCurrentStepIndex] = useState(0)

  const [keyCode, setKeyCode] = useState({ code: null, qrCode: null })
  const [backupCodes, setBackupCodes] = useState()

  const [alert, setAlert] = useState(null)

  const verifySecurityCode = async (code) => {
    try {
      const { data, status } = await fetchWithAuth('auth/2fa/verify-setup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      })
      if (status === 200 && data.backupCodes) {
        setBackupCodes(data.backupCodes)
        setCurrentStepIndex(currentStepIndex + 1)
      } else {
        if (response.status === 401) {
          navigate('/login', { state: { alert: data.message } })
        } else {
          setAlert({
            text:
              data.message ??
              'Could not verify security code. Try again later.',
            color: 'error',
          })
        }
      }
    } catch (error) {
      setAlert({
        text: 'Could not verify security code. Try again later.',
        color: 'error',
      })
      console.error('Could not verify code', error)
    }
  }

  useEffect(() => {
    if (location.state?.twoFaEnabled == null) {
      navigate('/')
    }
  })

  const setNewKeyCode = (code) => {
    setKeyCode(code)
    moveToStep(currentStepIndex + 1)
    setTimeout(() => {
      setKeyCode(null)
    }, KEYCODE_LIFETIME)
  }

  const moveToStep = (step) => {
    setCurrentStepIndex(step)
  }

  const steps = [
    {
      title: 'Set up two-factor authentication',
      instructions: (
        <>
          You will need an authenticator app like{' '}
          <a
            target="_blank"
            href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
          >
            Google Authenticator
          </a>{' '}
          to complete setting up two-factor authentication. <br />
          <br />
          Scan the QR code or copy the key code to your authenticator app to
          activate 2FA.
        </>
      ),
      skip2faPossible: true,
      children: (
        <TwoFactorAuthSetupCode
          previousCode={keyCode}
          onConfirm={(code) => {
            setNewKeyCode(code)
          }}
          showAlert={(newAlert) => setAlert(newAlert)}
        />
      ),
    },
    {
      showLogo: true,
      title: 'Enter security code',
      skip2faPossible: true,
      instructions: (
        <>
          Enter the code from your authenticator app for the{' '}
          <b>{user?.user?.email}</b> account.
        </>
      ),
      children: (
        <EnterSecurityCode
          alert={alert}
          onConfirm={(code) => verifySecurityCode(code)}
          useBackupCodes={false}
        />
      ),
      onBackNavigation: () => moveToStep(currentStepIndex - 1),
    },
    {
      title: 'Save backup codes',
      instructions: (
        <>
          Download and save these backup codes in a safe place, as they are only
          shown once.
          <br />
          <br />
          In case you are unable to use your phone or authenticator, you can use
          these codes to sign in.
        </>
      ),
      children: (
        <SaveBackupCodes
          onConfirm={() => navigate('/')}
          backupCodes={backupCodes}
        />
      ),
      alert: {
        variant: 'success',
        text: 'Two-factor authentication was successfully enabled.',
      },
      showBackNavigation: false,
    },
  ]

  return (
    <AuthenticationPageTemplate
      alert={alert}
      {...steps[currentStepIndex]}
    ></AuthenticationPageTemplate>
  )
}

export default TwoFactorAuthSetup
